import React from "react";
import { Link } from "react-router-dom";
import { useStore } from "../lib/store";
import { formatINR, discountPct, track } from "../lib/api";
import { Heart, Star, ShoppingBag, Zap } from "lucide-react";

export default function ProductCard({ p, priority = false }) {
  const { addToCart, toggleWishlist, inWishlist } = useStore();
  const off = discountPct(p.price, p.mrp);
  const wished = inWishlist(p.slug);
  const outOfStock = !p.in_stock;
  const onSale = p.mrp && p.mrp > p.price;

  return (
    <article className="group relative flex flex-col bg-surface rounded-2xl overflow-hidden h-full" data-testid={`product-card-${p.slug}`}>
      <div className="relative">
        <Link to={`/products/${p.slug}`} className="block aspect-square overflow-hidden" aria-label={p.name}>
          <img src={p.image} alt={p.image_alt || p.name} width="400" height="400"
            loading={priority ? "eager" : "lazy"} fetchPriority={priority ? "high" : "auto"}
            className="w-full h-full object-cover transition-transform duration-1000 ease-[cubic-bezier(.3,1,.3,1)] group-hover:scale-[1.04]" />
        </Link>
        <div className="absolute top-3 left-3 flex flex-col gap-1.5 text-[11px] leading-none">
          {p.tags?.includes("new") ? <span className="bg-mint text-white px-2.5 py-1.5 rounded-full">New</span>
            : onSale && <span className="bg-coral text-white px-2.5 py-1.5 rounded-full">Sale</span>}
                    {outOfStock && <span className="bg-[#ADADAD] text-white px-2.5 py-1.5 rounded-full">Sold out</span>}
        </div>
        <div className="absolute top-3 right-3 flex flex-col gap-2">
          {!outOfStock && (p.needs_selection ? (
            <Link to={`/products/${p.slug}`} aria-label={`Choose options for ${p.name}`} data-testid={`quick-bag-${p.slug}`}
              className="w-9 h-9 rounded-full bg-white flex items-center justify-center text-charcoal hover:bg-forest hover:text-white transition-colors"><ShoppingBag className="w-4 h-4" /></Link>
          ) : (
            <button onClick={() => addToCart({ slug: p.slug, name: p.name, images: [{ url: p.image, alt: p.image_alt }] }, { id: p.swatches[0]?.name, name: p.swatches[0]?.name, price: p.price, mrp: p.mrp })}
              aria-label={`Add ${p.name} to bag`} data-testid={`quick-bag-${p.slug}`}
              className="w-9 h-9 rounded-full bg-white flex items-center justify-center text-charcoal hover:bg-forest hover:text-white transition-colors"><ShoppingBag className="w-4 h-4" /></button>
          ))}
          <button
            data-testid={`wishlist-toggle-${p.slug}`}
            onClick={() => toggleWishlist(p)}
            aria-label={wished ? `Remove ${p.name} from wishlist` : `Add ${p.name} to wishlist`}
            aria-pressed={wished}
            className={`w-9 h-9 rounded-full bg-white flex items-center justify-center transition-all duration-300 lg:opacity-0 lg:translate-y-1 group-hover:opacity-100 group-hover:translate-y-0 ${wished ? "text-coral lg:opacity-100 lg:translate-y-0" : "text-charcoal"}`}>
            <Heart className="w-4 h-4" fill={wished ? "currentColor" : "none"} />
          </button>
        </div>
        {p.tags?.includes("bestseller") && !outOfStock && (
          <div className="absolute inset-x-0 bottom-0 bg-white/95 text-[11px] leading-none py-2 overflow-hidden lg:group-hover:opacity-0 transition-opacity" aria-hidden="true">
            <div className="flex gap-6 whitespace-nowrap animate-marquee w-max">
              {Array.from({ length: 10 }).map((_, i) => <span key={i} className="inline-flex items-center gap-1.5"><Zap className="w-3 h-3 fill-[#F5B800] text-[#F5B800]" /> Selling Fast</span>)}
            </div>
          </div>
        )}
        {!outOfStock && (
          <div className="hidden lg:block absolute inset-x-3 bottom-3 transition-all duration-300 lg:opacity-0 lg:translate-y-2 group-hover:opacity-100 group-hover:translate-y-0">
            {p.needs_selection ? (
              <Link to={`/products/${p.slug}`} data-testid={`choose-options-${p.slug}`}
                onClick={() => track("select_item", { item_id: p.slug })}
                className="block text-center bg-white text-charcoal text-sm h-11 leading-[44px] rounded-full hover:bg-forest hover:text-white transition-colors">
                Choose Options
              </Link>
            ) : (
              <button data-testid={`quick-add-${p.slug}`}
                onClick={() => addToCart({ slug: p.slug, name: p.name, images: [{ url: p.image, alt: p.image_alt }] }, { id: p.swatches[0]?.name, name: p.swatches[0]?.name, price: p.price, mrp: p.mrp })}
                className="w-full bg-white text-charcoal text-sm h-11 rounded-full hover:bg-forest hover:text-white transition-colors">
                Quick Add
              </button>
            )}
          </div>
        )}
      </div>

      <div className="px-4 pt-3 pb-4">
        <p className="label">{(p.category || "haven hives").replace(/-/g, " ")}</p>
        <h3 className="font-body text-[15px] leading-snug mt-1.5">
          <Link to={`/products/${p.slug}`} className="hover:text-coral transition-colors">{p.name}</Link>
        </h3>
        <div className="flex items-center gap-2 mt-1.5 text-[15px]">
          <span className={onSale ? "text-coral" : ""} data-testid={`price-${p.slug}`}>{formatINR(p.price)}</span>
          {onSale && <span className="text-subtext/70 line-through text-sm">{formatINR(p.mrp)}</span>}
          {off > 0 && <span className="text-xs text-subtext">({off}% off)</span>}
        </div>
        <div className="flex items-center justify-between mt-2 min-h-[16px]">
          {p.swatches?.length > 0 ? (
            <div className="flex items-center gap-1.5" role="list" aria-label="Available colours">
              {p.swatches.slice(0, 4).map((s) => (
                <span key={s.name} role="listitem" title={s.name} className="w-3.5 h-3.5 rounded-full border border-line inline-block" style={{ background: s.swatch }}>
                  <span className="sr-only">{s.name}</span>
                </span>
              ))}
            </div>
          ) : <span />}
          {p.rating ? (
            <span className="flex items-center gap-1 text-xs text-subtext">
              <Star className="w-3.5 h-3.5 fill-charcoal text-charcoal" /> {p.rating.avg} ({p.rating.count})
            </span>
          ) : null}
        </div>
      </div>
    </article>
  );
}
